import React, { useState } from "react";
import { MenuItem } from "@mui/material";
import { C } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { toBillingError } from "../billing-api";

/**
 * One dialog for adding or editing a service-master lookup — group,
 * classification, service type or sample type. Shared by the quick-add buttons
 * on the Service Master screen and by the Manage masters panel so both look and
 * behave the same.
 *
 * fields: [{ key, label, required, placeholder, helper, select, options:[{id,name}], initial, disabled }]
 */
export default function ServiceMasterMasterFormModal({
  title,
  fields,
  note,
  submitLabel = "Save",
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, f.initial != null ? String(f.initial) : ""]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const required = fields.filter((f) => f.required && !String(form[f.key] || "").trim());
    if (required.length) { setError(`${required[0].label} is required`); return; }
    setSaving(true); setError("");
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      setError(toBillingError(err, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "420px", maxWidth: "100%", background: IPD_COLORS.cardBg, borderRadius: "12px", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "15px", fontWeight: 800, color: C.text }}>{title}</span>
          <IpdButton variant="icon" onClick={onClose} sx={{ minWidth: 0 }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </IpdButton>
        </div>
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          {fields.map((f) => (
            <div key={f.key}>
              <IpdFormField label={f.label} required={f.required}>
                {f.select ? (
                  <IpdTextField select fullWidth value={form[f.key]} onChange={set(f.key)} disabled={f.disabled} SelectProps={{ displayEmpty: true }}>
                    <MenuItem value="">{f.placeholder || "Select…"}</MenuItem>
                    {(f.options || []).map((o) => <MenuItem key={o.id} value={String(o.id)}>{o.name}</MenuItem>)}
                  </IpdTextField>
                ) : (
                  <IpdTextField fullWidth value={form[f.key]} onChange={set(f.key)} placeholder={f.placeholder} disabled={f.disabled} />
                )}
              </IpdFormField>
              {f.helper && (
                <div style={{ fontSize: "11px", color: C.textSub, marginTop: "4px" }}>{f.helper}</div>
              )}
            </div>
          ))}
          {note && (
            <div style={{ fontSize: "11.5px", color: C.textSub, lineHeight: 1.5, background: IPD_COLORS.pageBg, borderRadius: "8px", padding: "10px 12px" }}>
              {note}
            </div>
          )}
          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: 600 }}>{error}</div>}
        </div>
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <IpdButton variant="outlined" onClick={onClose}>Cancel</IpdButton>
          <IpdButton onClick={submit} loading={saving}>{submitLabel}</IpdButton>
        </div>
      </div>
    </div>
  );
}
